import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { IngestionFlowStepServiceProps } from '../../IngestionFlow';
import { IngestTransformFileStep } from './IngestTransformFileStep';
import { useUnifiedIngestTransformFile } from 'src/products/ingestion/services';
import { useUnifiedIngestWriteTemplates } from 'src/products/models-library/services/dsp/useUnifiedIngestWriteTemplates';
import { snakeCase } from 'lodash';
import { useCherreState } from '@cherre-frontend/data-fetching';
import { extractionTemplateState } from 'src/products/models-library/pages/extraction-template-create';
import { useAppContext } from '@cherre-frontend/core';
import { ToolInput } from './types';

const getWebSocketUrl = () => {
  const parsedUrl = new URL(window.location.origin);
  const isDevelopment = process.env.NODE_ENV === 'development';
  const protocol =
    parsedUrl.protocol !== 'https:' && isDevelopment ? 'ws:' : 'wss:';
  const wsUrl = `${protocol}//${parsedUrl.host}`;
  return wsUrl;
};
export const IngestTransformFileStepService: React.FC<
  IngestionFlowStepServiceProps
> = ({ flowState, setFlowState, nextStep, config }) => {
  if (!flowState.fileId) {
    throw new Error('Need to upload a file before transforming');
  }
  const ctx = useAppContext();
  const { id, domOwner, organizationSlug } = useSelector(
    (state) => state.user.profile.value
  );

  const webSocketUrl = `${getWebSocketUrl()}/api/ingest/transform?owner=${domOwner}`;
  const transformFile = useUnifiedIngestTransformFile();
  const writeTemplate = useUnifiedIngestWriteTemplates();
  const [templateData] = useCherreState(extractionTemplateState);

  const onTranformingFileContinue = useCallback(
    async (
      toolSteps: {
        selected_tool_name: string;
        tool_input: Record<string, ToolInput>[];
      }[]
    ) => {
      if (!flowState.fileId) {
        throw new Error('No file ID found');
      }

      const templateId = snakeCase(
        templateData?.templateName
      ).toLocaleLowerCase();

      try {
        const steps: Record<
          string,
          { function_name: string; function_input: Record<string, ToolInput> }
        > = toolSteps.reduce((acc, step, index) => {
          const { selected_tool_name, tool_input } = step;
          return {
            ...acc,
            [`step${index + 1}`]: {
              function_name: selected_tool_name,
              function_input: tool_input,
            },
          };
        }, {});

        await writeTemplate({
          params: {
            owner_name: domOwner ?? '',
            template_id: templateId,
            user_id: id?.toString() ?? '',
            org_slug: organizationSlug ?? '',
            template_display_name: templateData?.templateName ?? '',
            source_system: snakeCase(
              templateData?.erpSystem ?? ''
            ).toLowerCase(),
            target_table: templateData?.targetTableDetails?.name,
            file_type: snakeCase(templateData?.fileType ?? '').toLowerCase(),
            is_production: true,
            steps,
          },
        });

        await transformFile({
          params: {
            file_id: flowState.fileId,
            owner_name: domOwner ?? '',
            template_id: templateId,
          },
        });

        nextStep?.();
      } catch (error) {
        ctx.showSnackbar({
          message: 'Error creating and applying transformation template',
          type: 'error',
        });
      }
    },
    [
      flowState.fileId,
      domOwner,
      transformFile,
      setFlowState,
      nextStep,
      config?.templateId,
    ]
  );

  return (
    <IngestTransformFileStep
      onClickSubmit={onTranformingFileContinue}
      fileId={flowState.fileId}
      webSocketUrl={webSocketUrl}
    />
  );
};
